import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input
} from '@angular/core';

import { PropertyType } from '@ui/shared/model';
import { TranslatePipe } from '@ngx-translate/core';
import { ImmomioTooltipDirective } from '@immomio/ui/tooltip';
import { SCREEN_LARGE_MIN } from '../../../config/screen-sizes.config';
import { buildBEMClassNamesByGivenBaseClassAndFlags } from '../../../utils/class-names';
import { isTouchScreen } from '../../../utils/touch-screen';
import {
  CurrentStatusConfig,
  PipelineSteps
} from './status-pipeline-base.config';

@Component({
  selector: 'app-status-pipeline',
  templateUrl: './status-pipeline-base.component.html',
  styleUrls: ['./status-pipeline-base.component.scss'],
  imports: [ImmomioTooltipDirective, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusPipelineBaseComponent {
  readonly pageView = input(false);
  readonly steps = input<PipelineSteps>(undefined);
  readonly currentStatus = input<CurrentStatusConfig>(undefined);
  readonly propertyType = input<PropertyType>(PropertyType.FLAT);

  readonly pipelineSteps = computed(() => {
    const {
      stepId: currentStatusStepId,
      tooltip: currentStatusTooltip,
      label: currentStatusLabel,
      showAsFullscreen: currentStatusShowAsFullscreen
    } = this.currentStatus().pipeline;

    return this.steps().map((step, stepId) => {
      const isActive = stepId === currentStatusStepId;
      return {
        ...step,
        isActive,
        label: isActive && currentStatusLabel ? currentStatusLabel : step.label,
        hasPassed:
          currentStatusShowAsFullscreen || stepId < currentStatusStepId,
        tooltip: isActive ? currentStatusTooltip : step.label
      };
    });
  });

  public baseClass = 'status-pipeline-base';

  public get narrowScreen(): boolean {
    return !this.pageView() || window.innerWidth < SCREEN_LARGE_MIN;
  }

  // a tap opens it at once; on hover, wait so passing over the steps stays quiet
  protected readonly tooltipOpenDelay = isTouchScreen() ? 0 : 1000;

  public getClassName(): string {
    return buildBEMClassNamesByGivenBaseClassAndFlags(this.baseClass, {
      'page-view': this.pageView()
    });
  }

  public getStepListClassName(hidden: boolean): string {
    return buildBEMClassNamesByGivenBaseClassAndFlags(
      `${this.baseClass}__step-list`,
      {
        hidden
      }
    );
  }

  public getStepItemClassName(
    first: boolean,
    last: boolean,
    active: boolean,
    passed: boolean
  ): string {
    return buildBEMClassNamesByGivenBaseClassAndFlags(
      `${this.baseClass}__step-item`,
      {
        first,
        last,
        active,
        passed
      }
    );
  }

  public getFullscreenStatusClassName(
    hide: boolean,
    isTenant: boolean
  ): string {
    return buildBEMClassNamesByGivenBaseClassAndFlags(
      `${this.baseClass}__fullscreen-status`,
      {
        'color-grey': hide,
        'color-green': isTenant
      }
    );
  }

  public getStepWrapperClassName(first: boolean, last: boolean): string {
    return buildBEMClassNamesByGivenBaseClassAndFlags(
      `${this.baseClass}__step-wrapper`,
      {
        first,
        last
      }
    );
  }
}
